import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Package,
  Clock,
  ChevronRight,
  ArrowRight,
  Truck,
  CheckCircle2,
  XCircle,
  FileText,
  AlertTriangle,
  Lock,
} from 'lucide-react'
import { api, formatMoney, type OrderSummary } from '../api/client'
import { useSession } from '../state/session'
import { useTranslation } from '../i18n/LanguageContext'

export function Orders() {
  const { signedIn } = useSession()
  const { t, isHindi } = useTranslation()
  const [orders, setOrders] = useState<OrderSummary[]>([])
  const [cursor, setCursor] = useState<string | undefined>(undefined)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!signedIn) {
      setLoading(false)
      return
    }

    api.orders
      .list()
      .then((page) => {
        setOrders(page.items)
        setCursor(page.nextCursor)
        setHasMore(page.hasMore)
      })
      .finally(() => setLoading(false))
  }, [signedIn])

  async function loadMore() {
    const page = await api.orders.list(cursor)
    setOrders((current) => [...current, ...page.items])
    setCursor(page.nextCursor)
    setHasMore(page.hasMore)
  }

  if (!signedIn) {
    return (
      <div className="card mx-auto my-12 max-w-md p-8 text-center shadow-lg">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700">
          <Lock className="h-8 w-8" />
        </div>
        <h1 className="font-heading text-2xl font-bold text-stone-900">{t('orders_auth_title')}</h1>
        <p className="mt-2 text-xs leading-relaxed text-stone-500">
          {t('orders_auth_desc')}
        </p>
        <Link
          to="/sign-in"
          state={{ from: '/orders', message: isHindi ? 'ऑर्डर्स देखने के लिए साइन इन करें' : 'Sign in to see your past orders' }}
          className="btn-primary mt-6 inline-flex w-full items-center justify-center gap-2 py-3 text-sm font-bold shadow-md shadow-emerald-700/20"
        >
          <span>{t('orders_auth_btn')}</span>
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl py-12 text-center">
        <div className="mx-auto mb-3 h-10 w-10 animate-spin rounded-full border-4 border-emerald-600 border-t-transparent" />
        <p className="text-sm font-medium text-stone-500">{t('orders_loading')}</p>
      </div>
    )
  }

  if (orders.length === 0) {
    return (
      <div className="card mx-auto my-12 max-w-md p-8 text-center sm:p-12">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-stone-100 text-stone-400">
          <Package className="h-8 w-8" />
        </div>
        <h1 className="font-heading text-2xl font-bold text-stone-900">{t('orders_empty_title')}</h1>
        <p className="mt-2 text-xs text-stone-500 leading-relaxed">
          {t('orders_empty_desc')}
        </p>
        <Link to="/" className="btn-primary mt-6 inline-flex items-center gap-2 py-3 px-6 text-sm font-bold">
          <span>{t('orders_start_btn')}</span>
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-black text-stone-900 sm:text-3xl">{t('orders_title')}</h1>
        <p className="text-xs text-stone-500">
          {isHindi ? 'वर्तमान डिलीवरी ट्रैक करें और आधिकारिक GST रसीदें देखें' : 'Track current deliveries and view official GST receipts'}
        </p>
      </div>

      <ul className="space-y-4">
        {orders.map((order) => (
          <li
            key={order.orderId}
            className="card card-hover group overflow-hidden border-stone-200/90 bg-white transition-all p-5"
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-stone-100 pb-3">
              <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                  <Package className="h-5 w-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-heading text-sm font-bold text-stone-900">
                      #{order.orderNumber}
                    </span>
                    <StatusBadge status={order.status} />
                  </div>
                  <span className="text-[11px] text-stone-400">
                    {isHindi ? 'ऑर्डर तिथि: ' : 'Placed on '}
                    {new Date(order.placedAt).toLocaleDateString(isHindi ? 'hi-IN' : 'en-IN', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
              </div>

              <div className="text-right sm:text-right">
                <span className="money text-base font-black text-stone-900">
                  {formatMoney(order.totalPaise)}
                </span>
                <span className="block text-[11px] text-stone-400">
                  {isHindi
                    ? `${order.itemCount} सामान · ${order.paymentMethod === 'ONLINE' ? 'ऑनलाइन भुगतान' : 'कैश ऑन डिलीवरी'}`
                    : `${order.itemCount} ${order.itemCount === 1 ? 'item' : 'items'} · ${order.paymentMethod === 'ONLINE' ? 'Paid Online' : 'Cash on Delivery'}`}
                </span>
              </div>
            </div>

            <div className="mt-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-xs text-stone-600">
                <Truck className="h-4 w-4 text-emerald-600 shrink-0" />
                <span>
                  {order.fulfilmentType === 'PICKUP'
                    ? (isHindi ? 'स्टोर पिकअप' : 'Store Pickup')
                    : (isHindi ? 'एक्सप्रेस डिलीवरी' : 'Express Delivery')}
                  {order.window ? ` · ${isHindi ? 'स्लॉट: ' : 'Slot: '}${order.window.label}` : ''}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <a
                  href={api.invoice.url(order.orderId)}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-secondary py-1.5 px-3 text-xs font-semibold inline-flex items-center gap-1.5"
                >
                  <FileText className="h-3.5 w-3.5 text-stone-500" />
                  <span>{isHindi ? 'इनवॉइस' : 'Invoice'}</span>
                </a>

                <Link
                  to={`/orders/${order.orderId}`}
                  className="btn-primary py-1.5 px-3.5 text-xs font-bold inline-flex items-center gap-1"
                >
                  <span>{isHindi ? 'ट्रैक करें' : 'Track Details'}</span>
                  <ChevronRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>

            {order.totalsAdjusted && (
              <div className="mt-3 flex items-center gap-1.5 rounded-xl bg-amber-50 px-3 py-2 text-[11px] font-medium text-amber-900">
                <AlertTriangle className="h-3.5 w-3.5 text-amber-700 shrink-0" />
                <span>
                  {isHindi
                    ? 'सटीक तौल के बाद अंतिम राशि समायोजित की गई है।'
                    : 'The final amount was adjusted after precise pack weighing.'}
                </span>
              </div>
            )}
          </li>
        ))}
      </ul>

      {hasMore && (
        <div className="pt-4 text-center">
          <button
            type="button"
            className="btn-secondary px-6 py-2.5 text-xs font-bold"
            onClick={() => void loadMore()}
          >
            {isHindi ? 'और पुराने ऑर्डर्स देखें' : 'Show Older Orders'}
          </button>
        </div>
      )}
    </div>
  )
}

export function StatusBadge({ status }: { status: string }) {
  let isHindi = false
  try {
    const i18n = useTranslation()
    isHindi = i18n.isHindi
  } catch {
    // fallback
  }

  const config: Record<
    string,
    { labelEn: string; labelHi: string; bg: string; text: string; icon: typeof CheckCircle2 }
  > = {
    PLACED: { labelEn: 'Received', labelHi: 'प्राप्त हुआ', bg: 'bg-blue-50 border-blue-200', text: 'text-blue-800', icon: Clock },
    CONFIRMED: { labelEn: 'Confirmed', labelHi: 'कन्फर्म हुआ', bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-800', icon: CheckCircle2 },
    PACKING: { labelEn: 'Packing', labelHi: 'पैकिंग जारी', bg: 'bg-amber-50 border-amber-200', text: 'text-amber-800', icon: Package },
    READY_FOR_PICKUP: { labelEn: 'Ready for Pickup', labelHi: 'पिकअप हेतु तैयार', bg: 'bg-teal-50 border-teal-200', text: 'text-teal-800', icon: CheckCircle2 },
    OUT_FOR_DELIVERY: { labelEn: 'Out for Delivery', labelHi: 'डिलीवरी के लिए निकला', bg: 'bg-indigo-50 border-indigo-200', text: 'text-indigo-800', icon: Truck },
    DELIVERED: { labelEn: 'Delivered', labelHi: 'डिलीवर हुआ', bg: 'bg-emerald-100 border-emerald-300', text: 'text-emerald-900', icon: CheckCircle2 },
    CANCELLED: { labelEn: 'Cancelled', labelHi: 'रद्द', bg: 'bg-stone-100 border-stone-200', text: 'text-stone-700', icon: XCircle },
  }

  const matched = config[status] ?? {
    labelEn: status,
    labelHi: status,
    bg: 'bg-stone-100 border-stone-200',
    text: 'text-stone-700',
    icon: Clock,
  }

  const Icon = matched.icon
  const label = isHindi ? matched.labelHi : matched.labelEn

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold ${matched.bg} ${matched.text}`}
    >
      <Icon className="h-3 w-3" />
      <span>{label}</span>
    </span>
  )
}
